/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { SaleOrderCreateExt, SaleOrderItem, useSaleOrderCreate } from 'src/services/saleOrder';
import { useAvailableIncotermsQuery } from 'src/services/incoterm';
import { useCodSettingsQuery } from 'src/services/codSettings';
import FileUpload from 'src/components/ui/FileUpload/FileUpload';
import { useTranslation } from 'react-i18next';
import Input from 'src/components/ui/Input/Input';
import Textarea from 'src/components/ui/Textarea/Textarea';
import Toggle from 'src/components/ui/Toggle/Toggle';
import Select, { Option } from 'src/components/ui/Select/Select';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { toast } from 'react-toastify';
import { StockItem } from 'src/services/inventory';
import Tooltip from 'src/components/ui/Tooltip/Tooltip';
import ContainerHead from 'src/components/ui/ContainerOverlay/ContainerHead/ContainerHead';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import PartnerSelect from 'src/components/layouts/PartnerSelect/PartnerSelect';
import { useWarehouses } from 'src/services/warehouse';
import CarrierSelect from 'src/components/ui/CarrierSelect/CarrierSelect';
import { useNavigate } from 'react-router';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { mergeOrderLinesWithSelection } from 'src/hooks/useOutboundOrder';
import styles from './CreateSaleOrderOverlay.module.scss';
import { SelectedProductsProduct, SelectProductsFromInventory } from '../SelectProducts/SelectProducts';
import ProductTable from '../OutboundOverlay/ProductTable/ProductTable';
import { useOverlay } from '../../../providers/OverlayProvider';
import { validateEmail } from '../../../utils/general';

const CreateSaleOrderOverlay: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [newOrder, setNewOrder] = useState<SaleOrderItem>({} as SaleOrderItem);
  const [showSelectProducts, setShowSelectProducts] = useState(false);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);

  const { mutateAsync: createSaleOrder } = useSaleOrderCreate();
  const { onClose } = useOverlay();
  const navigate = useNavigate();

  const { warehouses } = useWarehouses();
  const { data: incoterms } = useAvailableIncotermsQuery();
  const { data: codSettings } = useCodSettingsQuery();

  const incotermOption = (id: number | null | undefined): Option | undefined => {
    const incoterm = incoterms?.find(i => i.id === id);
    return incoterm ? { label: `${incoterm.code} — ${incoterm.name}`, value: String(incoterm.id) } : undefined;
  };

  const codCurrencyOption = (id: number | null | undefined): Option | undefined => {
    const currency = codSettings?.currencies.find(c => c.id === id);
    return currency ? { label: `${currency.code} — ${currency.name}`, value: String(currency.id) } : undefined;
  };

  const warehouseOptions = (warehouses || []).map(warehouse => ({
    value: warehouse.id.toString(),
    label: `${warehouse.name} (${warehouse.country_code})`,
  }));

  const updateSaleOrderItem = (key: string, value: any) => {
    if (key) {
      setNewOrder({
        ...newOrder,
        [key]: value,
      });
    }
  };

  const handleProductUpdate = (products: SelectedProductsProduct[]) => {
    // merge instead of rebuilding: lines the user already configured keep their
    // quantity and lot; only newly selected products get a fresh line
    updateSaleOrderItem('order_lines', mergeOrderLinesWithSelection((newOrder.order_lines ?? []) as any[], products));
    setShowSelectProducts(false);
  };

  const handleProductDelete = (id: number) => {
    setShowSelectProducts(false);
    setNewOrder(prev => {
      return {
        ...prev,
        order_lines: prev.order_lines.filter(line => +line.id !== id),
      };
    });
  };

  const handleClose = () => {
    setIsCloseConfirmationOpen(true);
  };

  const handleConfirmClose = (confirmed: boolean) => {
    if (confirmed) {
      if (isCloseConfirmationOpen) {
        onClose();
      }
    }

    setIsCloseConfirmationOpen(false);
  };

  /**
   * Submit new sale order
   */
  const submit = async () => {
    setLoading(true);

    try {
      const body: SaleOrderCreateExt = {
        name: newOrder.name,
        is_palette: newOrder.is_palette,
        partner: newOrder.partner,
        warehouse_id: newOrder.warehouse_id,
        order_lines: (newOrder?.order_lines || [])
          .filter(e => e.quantity > 0)
          .map(orderLine => {
            const product = orderLine.product as StockItem;
            return {
              product_id: product.product_id,
              quantity: orderLine.quantity,
              applied_quality_checks: [],
              lot_id: (orderLine as any).lot?.id || null,
              discount: (orderLine as any).discount || 0,
              unit_price: (orderLine as any).unit_price || 0,
            };
          }),
        order_note: newOrder.order_note,
        attachments: newOrder.attachments,
        carrier_id: newOrder.carrier_id,
        picking_policy: null,
        is_self_collection: newOrder.is_self_collection ?? false,
        incoterm_id: newOrder.incoterm_id ?? null,
        is_cod: newOrder.is_cod ?? false,
        cod_amount: newOrder.cod_amount ?? null,
        cod_currency_id: newOrder.cod_currency_id ?? null,
      };

      const res = await createSaleOrder({
        body: body as any,
      });

      if (res) {
        if (onClose) onClose();
        toast.success(t('success.create_sale_order_success'));
        if (res.id) {
          navigate(`/outbound/${res.id}`);
        }
      } else {
        toast.error(t('errors.create_sale_order_failed'), { autoClose: 5000 });
      }
    } catch (error: any) {
      const detail = error?.detail || error?.message || '';
      let message = detail || t('errors.create_sale_order_failed');
      if (typeof detail === 'string' && detail.includes('INCOTERM_NOT_ENABLED')) {
        message = t('errors.incoterm_not_enabled');
      } else if (typeof detail === 'string' && detail.includes('COD_NOT_ENABLED')) {
        message = t('errors.cod_not_enabled');
      }
      toast.error(message, { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const validateEntry = () => {
    const { partner } = newOrder;

    if (!partner) return false;
    if (
      !partner.name ||
      !partner.address.country_code ||
      !partner.address.street ||
      !partner.address.zip_code ||
      !partner.address.city ||
      !partner.email ||
      !validateEmail(partner.email)
    )
      return false;
    if (!newOrder.warehouse_id) return false;
    if (!newOrder.order_lines || newOrder.order_lines.length === 0) return false;
    if (newOrder.is_cod && (!newOrder.cod_amount || newOrder.cod_amount <= 0 || !newOrder.cod_currency_id))
      return false;

    return true;
  };

  const products: StockItem[] = useMemo(() => {
    return (
      newOrder?.order_lines?.filter(orderLine => orderLine.product).map(orderLine => orderLine.product as StockItem) ??
      []
    );
  }, [newOrder.order_lines]);

  return (
    <>
      <ContainerHead>
        <Button icon="keyboard-tab-right" onClick={handleClose} tone={ButtonToneProps.DESTRUCTIVE_TRANSPARENT} />

        <h3 className="typo-text-16--bold text-full truncate whitespace-nowrap">
          {t('deliveries.create_goods_issue')}
        </h3>
        <div className={classNames('ml-auto flex gap-x-16')}>
          <Button
            disabled={!validateEntry() || loading}
            title={t('general.save')}
            onClick={submit}
            tone={ButtonToneProps.PRIMARY_BLUE}
          />
          <Button title={t('general.cancel')} onClick={handleClose} tone={ButtonToneProps.SECONDARY} />
        </div>
      </ContainerHead>
      <ContainerBody>
        {showSelectProducts && (
          <SelectProductsFromInventory
            defaultValue={products as any[]}
            displayOptions={{ headline: true }}
            onUpdateSuccess={handleProductUpdate}
          />
        )}
        {!showSelectProducts && (
          <div className="flex flex-col gap-16 ">
            <div className={classNames(styles.info, 'grid grid-cols-4 items-center gap-16')}>
              <span className="typo-text-14--bold text-faded">
                {t('general.custom_id')}
                <Tooltip title={t('outbound.custom_id_info')} />
              </span>

              <Input
                className="col-span-3"
                placeholder={t('general.custom_id')}
                value={newOrder?.name}
                onChange={e => updateSaleOrderItem('name', e.target.value)}
              />
            </div>
            <div className={classNames(styles.info, 'grid grid-cols-4 items-center gap-16')}>
              <span className="typo-text-14--bold text-faded">{t('general.is_self_collection')}</span>
              <div className="col-span-3">
                <Toggle
                  checked={newOrder?.is_self_collection ?? false}
                  onToggle={checked => updateSaleOrderItem('is_self_collection', checked)}
                />
              </div>
            </div>
            <div className={classNames(styles.info, 'grid grid-cols-4 items-center gap-16')}>
              <span className="typo-text-14--bold text-faded">{t('navbar.warehouse')}*</span>
              <div className="flex flex-col flex-1 gap-16 col-span-3">
                <Select
                  className="w-full"
                  options={warehouseOptions}
                  defaultValue={warehouseOptions.find(option => option.value === String(newOrder.warehouse_id))}
                  onChange={option => {
                    updateSaleOrderItem('warehouse_id', Number.parseInt(option.value, 10));
                  }}
                />
              </div>
            </div>
            {!newOrder.is_self_collection && (
              <div className={classNames(styles.info, 'grid grid-cols-4 items-center gap-16')}>
                <span className="typo-text-14--bold text-faded">{t('general.carrier')}</span>
                <div className="flex flex-col flex-1 gap-16 col-span-3">
                  <CarrierSelect
                    defaultCarrier={newOrder.carrier_id}
                    onChange={carrier => {
                      updateSaleOrderItem('carrier_id', carrier);
                    }}
                  />
                </div>
              </div>
            )}
            {incoterms && incoterms.length > 0 && (
              <div className={classNames(styles.info, 'grid grid-cols-4 items-center gap-16')}>
                <span className="typo-text-14--bold text-faded">{t('outbound.incoterm')}</span>
                <div className="flex flex-col flex-1 gap-16 col-span-3">
                  <Select
                    id="create-order-incoterm"
                    options={incoterms.map(i => ({ label: `${i.code} — ${i.name}`, value: String(i.id) }))}
                    defaultValue={incotermOption(newOrder.incoterm_id)}
                    onChange={option => updateSaleOrderItem('incoterm_id', option ? Number(option.value) : null)}
                    isClearable
                    fullWidth
                  />
                </div>
              </div>
            )}
            {codSettings?.enabled && (
              <div className={classNames(styles.info, 'grid grid-cols-4 items-start gap-16')}>
                <span className="typo-text-14--bold text-faded">{t('outbound.cod_toggle')}</span>
                <div className="flex flex-col flex-1 gap-16 col-span-3">
                  <Toggle
                    checked={!!newOrder.is_cod}
                    onToggle={checked => {
                      updateSaleOrderItem('is_cod', checked);
                      if (!checked) {
                        updateSaleOrderItem('cod_amount', null);
                        updateSaleOrderItem('cod_currency_id', null);
                      }
                    }}
                  />
                  {newOrder.is_cod && (
                    <div className="grid grid-cols-2 gap-16">
                      <Input
                        id="create-order-cod-amount"
                        type="number"
                        min={0}
                        label={t('outbound.cod_amount')}
                        placeholder={t('outbound.cod_amount')}
                        value={newOrder.cod_amount ?? ''}
                        onChange={e =>
                          updateSaleOrderItem('cod_amount', e.target.value === '' ? null : Number(e.target.value))
                        }
                      />
                      <Select
                        id="create-order-cod-currency"
                        label={t('outbound.cod_currency')}
                        options={(codSettings.currencies ?? []).map(c => ({
                          label: `${c.code} — ${c.name}`,
                          value: String(c.id),
                        }))}
                        defaultValue={codCurrencyOption(newOrder.cod_currency_id)}
                        onChange={option =>
                          updateSaleOrderItem('cod_currency_id', option ? Number(option.value) : null)
                        }
                        isClearable
                        fullWidth
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={classNames(styles.info, 'grid grid-cols-4 items-baseline gap-16')}>
              <span className="typo-text-14--bold text-faded">{t('general.partner')}*</span>
              <div className="col-span-3">
                <PartnerSelect
                  selectedPartner={newOrder.partner}
                  onPartnerChange={partner => updateSaleOrderItem('partner', partner)}
                  domain="[('is_inbound_partner','=',False)]"
                  parentComponent="outbound"
                  withTaxId={false}
                />
              </div>
            </div>

            <div className={classNames(styles.info, 'grid grid-cols-4 items-center gap-16')}>
              <span className="typo-text-14--bold text-faded">{t('general.products')}*</span>
              <div className="col-span-3">
                <ProductTable
                  editable
                  displayOptions={{ status: false }}
                  orderLineData={newOrder?.order_lines as any}
                  onAddProduct={() => setShowSelectProducts(true)}
                  onOrderLineDelete={(productId: number) => handleProductDelete(productId)}
                  onChange={e => {
                    updateSaleOrderItem('order_lines', e);
                  }}
                />
              </div>
            </div>
            <div className={classNames(styles.info, `grid grid-cols-1 items-center gap-8`)}>
              <span className="typo-text-14--bold text-faded">
                {t('general.attachments')} <Tooltip title="Custom ID is optional" />
              </span>

              <FileUpload
                onChange={attachments => {
                  updateSaleOrderItem('attachments', attachments);
                }}
              />
            </div>
            <div className={classNames(styles.info, `grid grid-cols-1 items-center gap-8`)}>
              <span className="typo-text-14--bold text-faded whitespace-nowrap">
                {t('general.to_note')} <Tooltip title="Custom ID is optional" />
              </span>
              <Textarea
                placeholder={t('general.to_note')}
                value={newOrder?.order_note}
                onChange={e => updateSaleOrderItem('order_note', e.target.value)}
                className="col-span-1"
              />
            </div>
            <div className={classNames(styles.info, '')}>
              <Button
                disabled={!validateEntry()}
                title={t('general.save')}
                onClick={submit}
                tone={ButtonToneProps.PRIMARY_BLUE}
              />
            </div>
          </div>
        )}
        <ConfirmationModal
          isOpen={isCloseConfirmationOpen}
          title={t('outbound.exit_edit_mode')}
          description={t('outbound.exit_edit_mode_text')}
          onClose={handleConfirmClose}
        />
      </ContainerBody>
    </>
  );
};

export default CreateSaleOrderOverlay;
